import {
    DrawerRunnerRequest,
    useGetDrawerSystemQuery,
} from 'components/customer/Product/store/drawerRunnerApi';
import {useCallback, useEffect, useRef, useState} from 'react';
import {toNumber} from 'components/customer/Product/Drawer/helpers';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {useFormikContext} from 'formik';
import {isEqual} from 'lodash';
import {useProductContext} from 'contexts';
import {useSearchParams} from 'react-router-dom';
import Excel from 'shared/Excel';

// One runner request per drawer per distinct height, so wait for the typing to
// stop. The first set goes straight through, or a fresh page has no runners.
const REQUERY_DELAY = 400;

export const useRunnerRequestParams = (
    runnerIndex: number,
    isInnerDrawer: boolean,
    isDrawerComponent: boolean,
    drawerTypeFieldName: string,
    drawerDepth: string,
    drawerFaceHeightFieldName: string,
    fingerPullFieldName: string
) => {
    const {values} = useFormikContext<DrawerFields>();
    const {getMaterialOptions} = useProductContext<{
        getMaterialOptions: () => object;
    }>();

    const [searchParams] = useSearchParams();

    const [params, setParams] = useState<DrawerRunnerRequest>({});

    const {data: drawerSystems} = useGetDrawerSystemQuery(undefined, {
        skip: !isInnerDrawer,
    });

    const drawerRunnerProps = useRef<DrawerRunnerRequest>({});
    const getDrawerRunnerProps = useCallback((): DrawerRunnerRequest => {
        if (isDrawerComponent) {
            return {
                insertType: toNumber(values[String(drawerTypeFieldName)]),
                cabinetId: parseInt(searchParams.get('product')),
                isDrawerComponent,
            };
        }

        const drawer = values.drawers[Number(runnerIndex)];

        if (!drawer) {
            return {};
        }

        const materialOptions = getMaterialOptions();

        const drawerData = {
            insert_back_clearance: 0,
            insert_inner_drawer_set_back: 0,
        };
        if (isInnerDrawer && drawerSystems) {
            const drawerSystem = drawerSystems.find(
                (drawerSystem) => drawerSystem.id == drawer.insert_id
            );

            if (drawerSystem) {
                drawerData.insert_back_clearance =
                    drawerSystem?.backClearance || 0;
                drawerData.insert_inner_drawer_set_back =
                    drawerSystem?.innerDrawerSetback || 0;
            }
        }

        const depth = Excel.calculate<number>(drawerDepth, {
            ...values,
            ...materialOptions,
            ...drawerData,
        });

        const fingerPull = values[String(fingerPullFieldName)];

        return {
            insertType: toNumber(
                drawer[drawerTypeFieldName as keyof typeof drawer]
            ),
            faceHeight: toNumber(
                drawer[drawerFaceHeightFieldName as keyof typeof drawer]
            ),
            cabinetId: parseInt(searchParams.get('product')),
            depth,
            fingerPull: toNumber(fingerPull),
            isDrawerComponent,
        };
    }, [
        runnerIndex,
        isDrawerComponent,
        drawerTypeFieldName,
        drawerFaceHeightFieldName,
        fingerPullFieldName,
        searchParams,
        values,
        drawerDepth,
        drawerSystems,
    ]);

    const requery = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        const runnerProps = getDrawerRunnerProps();

        if (isEqual(runnerProps, drawerRunnerProps.current)) return;

        const first = Object.keys(drawerRunnerProps.current).length === 0;

        drawerRunnerProps.current = runnerProps;

        if (first) {
            setParams(runnerProps);
            return;
        }

        clearTimeout(requery.current);
        requery.current = setTimeout(
            () => setParams(runnerProps),
            REQUERY_DELAY
        );
    }, [getDrawerRunnerProps]);

    useEffect(() => () => clearTimeout(requery.current), []);

    return {
        params,
        // what the drawer asks for now, ahead of the debounced params
        latest: drawerRunnerProps,
    };
};
